<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Slider, Alert, Checkbox } from '@Shelly/shelly-design-system';
	//Types
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';
	// Helpers
	import { hasTilt, is_gen_2, real_id } from 'src/helpers/interface_helpers';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';

	export let controlOption: LocalControlOptions, device: Device;

	const _is_gen_2 = is_gen_2(device);
	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	const rollerIsCalibrated = roller_is_calibrated(device, status, settings);
	const hasSlat = hasTilt(device);
	let pos = controlOption.params.hasOwnProperty('pos') ? controlOption.params.pos : 50;
	let slat_pos = controlOption.params.hasOwnProperty('slat_pos') ? controlOption.params.slat_pos : 50;
	let slat_rel = controlOption.params.hasOwnProperty('slat_rel') ? controlOption.params.slat_rel : 0;

	let errorRollerNotCalibrated = {
		show: !rollerIsCalibrated,
		label: $format('alert_device_not_calibrated', {
			default: 'Your device is not calibrated',
		}),
	};

	const a11yPropsSliderPos = {
		sliderValueText: $format('current_cover_position_value', {
			values: { value: pos || 0 },
			default: `Current cover position value is ${pos || 0}`,
		}),
		ariaLabel: $format('label_set_cover_target_position', {
			default: 'Set Cover Target Position',
		}),
		ariaLabelValue: $format('current_cover_position_value', {
			values: { value: pos || 0 },
			default: `Current cover position value is ${pos || 0}`,
		}),
	};
</script>

<div>
	<Text class="mb-s" element="h2">
		{$format('label_cover_target_position', {
			default: 'Target position',
		})}
	</Text>
	{#if errorRollerNotCalibrated.show}
		<Alert
			class="mb-s"
			heading={errorRollerNotCalibrated.label}
			type="danger"
			cta={{
				title: $format('button_calibrate', {
					default: 'Calibrate',
				}),
			}}
			on:cta_click={() =>
				navigateToSetting('settings', _is_gen_2 ? 'calibration' : 'positioning_controls')}
		/>
	{/if}
	<div class="d-flex justify-content-start">
		<div style="margin-top: 2px;" class="mr-xs">
			<Checkbox
				size="m"
				class="pt-0"
				disabled={!rollerIsCalibrated}
				bind:checked={controlOption.attr.pos}
				on:click={() => {
					if (!controlOption.attr.pos) {
						controlOption.params.pos = pos;
					} else {
						pos = 50;
						delete controlOption.params.pos;
						controlOption.params = controlOption.params;
					}
				}}
				id="checkbox-5jzut32"
				aria-label={$format('label_cover_target_position', {
					default: 'Target position',
				})}
			/>
		</div>
		<Slider
			id="slider-agekcno"
			a11yProps={a11yPropsSliderPos}
			type="simple"
			value={pos}
			class="flex-1"
			disabled={!rollerIsCalibrated || !controlOption.attr.pos}
			on:value_change={(e) => {
				pos = e.detail.value;
				controlOption.params.pos = e.detail.value;
			}}
			title={$format('label_cover_slider_title', {
				values: { pos },
				default: 'Selected: {pos}%, Range: 0% (Fully closed) - 100 (Fully open)',
			})}
		/>
	</div>
	{#if hasSlat}
		<!-- todo abstract this for future gen 2++ covers -->
		<!-- {#if typeof controlOption.params.slat_pos !== 'undefined'} -->
		<Text class="mb-s mt-m" element="h2">
			{$format('label_cover_tilt_position', {
				default: 'Tilt position',
			})}
		</Text>
		<div class="mb-m d-flex justify-content-start">
			<div style="margin-top: 2px;" class="mr-xs">
				<Checkbox
					size="m"
					class="pt-0"
					disabled={!rollerIsCalibrated || controlOption.attr.slat_rel}
					bind:checked={controlOption.attr.slat_pos}
					on:click={() => {
						if (!controlOption.attr.slat_pos) {
							controlOption.params.slat_pos = slat_pos;
						} else {
							slat_pos = 50;
							delete controlOption.params.slat_pos;
							controlOption.params = controlOption.params;
						}
					}}
					id="checkbox-pv5o7zy"
					aria-label={$format('label_cover_tilt_position', {
						default: 'Tilt position',
					})}
				/>
			</div>
			<Slider
				id="slider-0rzf854"
				a11yProps={a11yPropsSliderPos}
				type="simple"
				class="flex-1"
				value={slat_pos}
				disabled={!rollerIsCalibrated || !controlOption.attr.slat_pos}
				on:value_change={(e) => {
					slat_pos = e.detail.value;
					controlOption.params.slat_pos = e.detail.value;
				}}
				title={$format('label_cover_slider_title', {
					values: { pos: slat_pos },
					default: 'Selected: {pos}%, Range: 0% (Fully closed) - 100 (Fully open)',
				})}
			/>
		</div>
		<!-- {/if}
	{#if typeof controlOption.params.slat_rel !== 'undefined'} -->
		<Text class="mb-s" element="h2">
			{$format('label_cover_relative_tilt_position', {
				default: 'Relative tilt position',
			})}
		</Text>
		<div class="d-flex justify-content-start">
			<div style="margin-top: 2px;" class="mr-xs">
				<Checkbox
					size="m"
					class="pt-0"
					disabled={!rollerIsCalibrated || controlOption.attr.slat_pos}
					bind:checked={controlOption.attr.slat_rel}
					on:click={() => {
						if (!controlOption.attr.slat_rel) {
							controlOption.params.slat_rel = slat_rel;
						} else {
							slat_rel = 0;
							delete controlOption.params.slat_rel;
							controlOption.params = controlOption.params;
						}
					}}
					id="checkbox-1i1rixj"
					aria-label={$format('label_cover_relative_tilt_position', {
						default: 'Relative tilt position',
					})}
				/>
			</div>
			<Slider
				id="slider-pw4rx25"
				a11yProps={a11yPropsSliderPos}
				type="simple"
				class="flex-1"
				value={slat_rel}
				disabled={!rollerIsCalibrated || !controlOption.attr.slat_rel}
				min={-100}
				on:value_change={(e) => {
					slat_rel = e.detail.value;
					controlOption.params.slat_rel = e.detail.value;
				}}
				title={$format('label_cover_slider_title', {
					values: { pos: slat_rel },
					default: 'Selected: {pos}%, Range: 0% (Fully closed) - 100 (Fully open)',
				})}
			/>
		</div>
		<!-- {/if} -->
	{/if}
</div>
